import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MedicationIcon from '@mui/icons-material/Medication'
import { Dialog, Box, IconButton, Tooltip, useMediaQuery, useTheme, Collapse, Typography, Chip } from '@mui/material'
import { memo, useMemo, useState } from 'react'
import { DRUG_TABLE_COLUMN_WEIGHTS, PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import {
  autoSuggestDrugAdviceUsingDrug,
  DosageSelectMenu,
  DrugSelectMenu,
  DrugTypeSelectMenu,
  DurationSelectMenu,
  FrequencySelectMenu,
  handleDrugAdviceChange,
  loadOptionsDrugs,
  NotesSelectMenu,
  nullAllValuesOfDrugAdvice,
  WhatTimeSelectMenu,
} from '../../PrescriptionMakingPageComponents'
import { PMPSingleRowBox, PMPSingleRowForDrugs } from '../../PrescriptionMakingPageStyles'
import { PRESCRIPTION_COLORS_MAP } from '../common/color-utils'
import PrescriptionPillViewInputComponent from '../common/PrescriptionPillViewInputComponent'
import DrugAdviceSelectedPillComponent from './components/DrugAdviceSelectedPillComponent'
import DrugAdviceContextForm from './DrugAdviceContextForm'
import DrugCreationForm from './DrugCreationForm'
import {
  getAndSetDrugWithRelatedNamesAndGenericDrugData,
  getAndSetDrugWithRelatedNamesForPillInput,
  getDrugAdviceEntitiesTitleToItemMap,
  getRenderDrugAdviceSubSectionsSet,
} from './utils'

const DrugAdviceInput = ({
  prescriptionViewSettings,
  drugsAdvice,
  setDrugsAdvice,
  handleAddDrugAdviceItem,
  handleDeleteDrugAdviceItem,
  clinicId,
  clinicBrandId,
  recommendedHealthEntityForDoctor,
  mentorProfileData,
  drugAdviceTypes,
  drugAdviceWhatTimeOptions,
  drugAdviceFrequencies,
  drugAdviceDurations,
  drugAdviceNotes,
  drugAdviceDosages,
  contextMenuSide,
  drugAdviceSubSections,
}) => {
  const recommendedDrugIds = recommendedHealthEntityForDoctor?.['drug-info']
  const convertedDataForPillUI = drugsAdvice?.map((item) => ({
    ...item?.drug,
    dosage: item?.dosage?.value,
    duration: item?.duration?.value,
    frequency: item?.frequency?.value,
    notes: item?.notes?.value,
    whatTime: item?.whatTime?.value,
    __isNew__: item?.__isNew__,
  }))
  const [showDrugCreationDialog, setShowDrugCreationDialog] = useState(false)
  const [newDrugInputValue, setNewDrugInputValue] = useState('')
  const dosagesMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceDosages,
      prescriptionViewSettings,
    })
  }, [drugAdviceDosages, prescriptionViewSettings])

  const drugAdviceWhatTimeTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceWhatTimeOptions,
      prescriptionViewSettings,
    })
  }, [drugAdviceWhatTimeOptions, prescriptionViewSettings])
  const drugAdviceFrequenciesTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceFrequencies,
      prescriptionViewSettings,
    })
  }, [drugAdviceFrequencies, prescriptionViewSettings])
  const drugAdviceDurationsTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceDurations,
      prescriptionViewSettings,
    })
  }, [drugAdviceDurations, prescriptionViewSettings])
  const drugAdviceNotesTitleToItemMap = useMemo(() => {
    return getDrugAdviceEntitiesTitleToItemMap({
      drugAdviceItems: drugAdviceNotes,
      prescriptionViewSettings,
    })
  }, [drugAdviceNotes, prescriptionViewSettings])

  function onChangeForPillInput(valueData) {
    setDrugsAdvice((prev) => {
      let dataToWorkWIth = valueData
      if (typeof valueData === 'function') {
        dataToWorkWIth = valueData(
          prev.map((drug) => {
            return {
              ...drug?.drug,
              dosage: drug?.dosage?.value,
              duration: drug?.duration?.value,
              frequency: drug?.frequency?.value,
              notes: drug?.notes?.value,
              whatTime: drug?.whatTime?.value,
              __isNew__: drug?.__isNew__,
            }
          }),
        )
      }
      return dataToWorkWIth.map((item) => {
        const foundDosage = dosagesMap?.[item?.dosage] || { __isNew__: true }

        const foundWhatTime = drugAdviceWhatTimeTitleToItemMap?.[item?.whatTime] || {
          __isNew__: true,
        }
        const foundFrequency = drugAdviceFrequenciesTitleToItemMap?.[item?.frequency] || {
          __isNew__: true,
        }
        const foundDuration = drugAdviceDurationsTitleToItemMap?.[item?.duration] || {
          __isNew__: true,
        }
        const foundNotes = drugAdviceNotesTitleToItemMap?.[item?.notes] || { __isNew__: true }
        return {
          drug: item,
          dosage: { ...foundDosage, label: item?.dosage, value: item?.dosage },
          whatTime: { ...foundWhatTime, label: item?.whatTime, value: item?.whatTime },
          frequency: { ...foundFrequency, label: item?.frequency, value: item?.frequency },
          duration: { ...foundDuration, label: item?.duration, value: item?.duration },
          notes: { ...foundNotes, label: item?.notes, value: item?.notes },
          __isNew__: item?.__isNew__,
        }
      })
    })
  }

  function handleSelectDrugAdviceItem(newValue, itemIdx) {

    if (Boolean(newValue)) {
      handleDrugAdviceChange('drug', itemIdx, newValue, setDrugsAdvice)
      autoSuggestDrugAdviceUsingDrug(itemIdx, newValue, setDrugsAdvice, drugAdviceTypes)
    } else {
      nullAllValuesOfDrugAdvice(itemIdx, setDrugsAdvice)
    }
  }

  function handleSelectDrugAdviceItemForPillInput(newValue, itemIdx) {
    onChangeForPillInput(newValue)
  }

  //for doc on select
  function handleChangeDrugItemForPillInput(allValues, itemIdx, newValue) {
    getAndSetDrugWithRelatedNamesForPillInput(
      mentorProfileData?.specializedCategories,
      newValue,
      allValues,
      clinicId,
      clinicBrandId,
      (val) => handleSelectDrugAdviceItemForPillInput(val),
    )
  }

  function closeDrugCreationDialog() {
    setShowDrugCreationDialog(false)
  }

  function createNewDrug(newValue) {
    setShowDrugCreationDialog(true)
    setNewDrugInputValue(newValue)
  }

  //for table drug select
  function handleChangeDrugItem(newValue, itemIdx, updateSelectedDrugStateOnly) {
    if (updateSelectedDrugStateOnly) {
      handleSelectDrugAdviceItem(newValue, itemIdx)
    } else {
      getAndSetDrugWithRelatedNamesAndGenericDrugData(
        mentorProfileData?.specializedCategories,
        newValue,
        clinicId,
        clinicBrandId,
        (val) => handleSelectDrugAdviceItem(val, itemIdx),
      )
    }
  }

  switch (prescriptionViewSettings?.prescriptionBuilderViewType) {
    case PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView:
      return (
        <>
          <PrescriptionPillViewInputComponent
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              recommendedDrugs: recommendedDrugIds,
            }}
            onChange={(valueArray, idx, newValue) => {
              handleChangeDrugItemForPillInput(valueArray, idx, newValue)
            }}
            onCreate={(inputValue) => {
              createNewDrug(inputValue)
            }}
            prescriptionViewSettings={prescriptionViewSettings}
            searchPlaceholder={'Search for drug'}
            debounceTimeout={200}
            getOptionLabel={(optn) => optn?.label}
            getOptionValue={(optn) => optn?.value}
            getOptionsResponse={loadOptionsDrugs}
            title="Rx"
            pillColor={PRESCRIPTION_COLORS_MAP.drugAdvice.color}
            highlightedPillColor={PRESCRIPTION_COLORS_MAP.drugAdvice.highlightColor}
            value={convertedDataForPillUI?.filter((item) => !!item?.title)}
            itemName={'drug'}
            ContextMenuFormComponent={DrugAdviceContextForm}
            contextMenuOpenSide={contextMenuSide}
            SelectedPillComponent={DrugAdviceSelectedPillComponent}
            showContextFormOnClickNewCondition={
              (item) => item?.__isNew__ || !item?.dosage || item?.duration || !item?.frequency
              // !item?.notes ||
              // !item?.whatTime
            }
            contextMenuAdditionalProps={{
              dosageOptions: drugAdviceDosages,
              durationOptions: drugAdviceDurations,
              frequencyOptions: drugAdviceFrequencies,
              whatTimeOptions: drugAdviceWhatTimeOptions,
              notesOptions: drugAdviceNotes,
              prescriptionViewSettings,
            }}
            useFullPageSearch={true}
          />
          <Dialog open={showDrugCreationDialog} onClose={closeDrugCreationDialog}>
            <DrugCreationForm
              inputString={newDrugInputValue}
              setDrugData={onChangeForPillInput}
              closeDialog={closeDrugCreationDialog}
            />
          </Dialog>
        </>
      )
    default:
      return (
        <DrugAdviceTableInput
          drugsAdvice={drugsAdvice}
          setDrugsAdvice={setDrugsAdvice}
          handleSelectDrugAdviceItem={handleChangeDrugItem}
          handleAddDrugAdviceItem={handleAddDrugAdviceItem}
          handleDeleteDrugAdviceItem={handleDeleteDrugAdviceItem}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          recommendedDrugIds={recommendedDrugIds}
          mentorProfileData={mentorProfileData}
          drugAdviceTypes={drugAdviceTypes}
          drugAdviceWhatTimeOptions={drugAdviceWhatTimeOptions}
          drugAdviceFrequencies={drugAdviceFrequencies}
          drugAdviceDurations={drugAdviceDurations}
          drugAdviceNotes={drugAdviceNotes}
          drugAdviceDosages={drugAdviceDosages}
          prescriptionViewSettings={prescriptionViewSettings}
          drugAdviceSubSections={drugAdviceSubSections}
        />
      )
  }
}

function DrugAdviceTableInput({
  drugsAdvice,
  setDrugsAdvice,
  handleAddDrugAdviceItem,
  handleSelectDrugAdviceItem,
  handleDeleteDrugAdviceItem,
  clinicId,
  clinicBrandId,
  recommendedDrugIds,
  mentorProfileData,
  drugAdviceTypes,
  drugAdviceWhatTimeOptions,
  drugAdviceFrequencies,
  drugAdviceDurations,
  drugAdviceNotes,
  drugAdviceDosages,
  prescriptionViewSettings,
  drugAdviceSubSections,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // For mobile: track which cards are expanded
  const [expandedCards, setExpandedCards] = useState({})

  const toggleCardExpansion = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  // Auto-expand card when drug is selected
  const handleDrugSelection = (newValue, drugAdviceRowIndex, updateSelectedDrugStateOnly) => {
    handleSelectDrugAdviceItem(newValue, drugAdviceRowIndex, updateSelectedDrugStateOnly)
    if (newValue && isMobile) {
      setExpandedCards((prev) => ({
        ...prev,
        [drugAdviceRowIndex]: true,
      }))
    }
  }


  // Mobile Card View
  if (isMobile) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative', // Create stacking context for dropdowns
          zIndex: 1,
        }}
      >
        {drugsAdvice?.map((drugAdviceRow, drugAdviceRowIndex) => {
          const hasDrug = Boolean(drugAdviceRow?.drug?.label)
          const isExpanded = expandedCards[drugAdviceRowIndex] ?? hasDrug

          return (
            <Box
              key={drugAdviceRowIndex}
              sx={{
                borderRadius: '16px',
                overflow: 'visible',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f7ff 100%)',
                border: hasDrug ? '2px solid #4338ca' : '1px solid rgba(67, 56, 202, 0.15)',
                boxShadow: hasDrug
                  ? '0 4px 20px rgba(67, 56, 202, 0.15)'
                  : '0 2px 8px rgba(67, 56, 202, 0.06)',
                transition: 'all 0.3s ease',
                animation: 'fadeSlideIn 0.3s ease-out',
                position: 'relative',
                zIndex: isExpanded ? 1000 + (100 - drugAdviceRowIndex) : 1, // Higher z-index for expanded cards, decreasing for lower cards
                '@keyframes fadeSlideIn': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  background: hasDrug
                    ? 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)'
                    : 'transparent',
                  borderBottom: hasDrug ? '1px solid rgba(67, 56, 202, 0.1)' : 'none',
                }}
              >
                {/* Drug Number Badge */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    background: hasDrug
                      ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                      : 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                    boxShadow: hasDrug
                      ? '0 2px 8px rgba(67, 56, 202, 0.3)'
                      : '0 1px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {drugAdviceRowIndex + 1}
                </Box>

                {/* Drug Name Field - Full Width */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
                    <DrugSelectMenu
                      clinicId={clinicId}
                      clinicBrandId={clinicBrandId}
                      selectedDrug={drugAdviceRow?.drug}
                      recommendedDrugs={recommendedDrugIds}
                      doctorSpecializations={mentorProfileData?.specializedCategories}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      setSelectedDrug={(newValue, updateSelectedDrugStateOnly) => {
                        handleDrugSelection(newValue, drugAdviceRowIndex, updateSelectedDrugStateOnly)
                      }}
                    />
                  )}
                </Box>

                {/* Expand/Collapse & Delete Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {hasDrug && (
                    <IconButton
                      onClick={() => toggleCardExpansion(drugAdviceRowIndex)}
                      size="small"
                      sx={{
                        color: '#4338ca',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.1)',
                        },
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => handleDeleteDrugAdviceItem(drugAdviceRowIndex, setDrugsAdvice)}
                    size="small"
                    sx={{
                      color: '#ef4444',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Expandable Content - Drug Details */}
              <Collapse in={isExpanded && hasDrug}>
                <Box
                  sx={{
                    p: 2,
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* Summary chips when collapsed - show current values */}
                  {!isExpanded && hasDrug && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {drugAdviceRow?.dosage?.label && (
                        <Chip
                          size="small"
                          label={drugAdviceRow.dosage.label}
                          sx={{ backgroundColor: 'rgba(67, 56, 202, 0.1)', color: '#4338ca' }}
                        />
                      )}
                      {drugAdviceRow?.frequency?.label && (
                        <Chip
                          size="small"
                          label={drugAdviceRow.frequency.label}
                          sx={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#16a34a' }}
                        />
                      )}
                      {drugAdviceRow?.duration?.label && (
                        <Chip
                          size="small"
                          label={drugAdviceRow.duration.label}
                          sx={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#d97706' }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Row 1: Type + Dosage */}
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'type')?.title || 'Type'}
                        color="#8b5cf6"
                      >
                        <DrugTypeSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedType={drugAdviceRow?.drugType}
                          options={drugAdviceTypes}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedType={(newValue) =>
                            handleDrugAdviceChange(
                              'drugType',
                              drugAdviceRowIndex,
                              newValue,
                              setDrugsAdvice,
                              drugAdviceTypes,
                            )
                          }
                        />
                      </MobileFieldWrapper>
                    )}
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'dosage')?.title || 'Dosage'}
                        color="#4338ca"
                      >
                        <DosageSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedDosage={drugAdviceRow?.dosage}
                          options={drugAdviceDosages}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedDosage={(newValue) =>
                            handleDrugAdviceChange('dosage', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                        />
                      </MobileFieldWrapper>
                    )}
                  </Box>

                  {/* Row 2: When + Frequency */}
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'when')?.title || 'When'}
                        color="#f59e0b"
                      >
                        <WhatTimeSelectMenu
                          allowEnteringValue={hasDrug}
                          selectedWhatTime={drugAdviceRow?.whatTime}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedWhatTime={(newValue) =>
                            handleDrugAdviceChange('whatTime', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                          options={drugAdviceWhatTimeOptions}
                        />
                      </MobileFieldWrapper>
                    )}
                    {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
                      <MobileFieldWrapper
                        label={drugAdviceSubSections?.find((item) => item?.uid === 'frequency')?.title || 'Frequency'}
                        color="#22c55e"
                      >
                        <FrequencySelectMenu
                          allowEnteringValue={hasDrug}
                          selectedFrequency={drugAdviceRow?.frequency}
                          prescriptionViewSettings={prescriptionViewSettings}
                          menuPortalTarget={document.body}
                          setSelectedFrequency={(newValue) =>
                            handleDrugAdviceChange('frequency', drugAdviceRowIndex, newValue, setDrugsAdvice)
                          }
                          options={drugAdviceFrequencies}
                        />
                      </MobileFieldWrapper>
                    )}
                  </Box>

                  {/* Row 3: Duration */}
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
                    <MobileFieldWrapper
                      label={drugAdviceSubSections?.find((item) => item?.uid === 'duration')?.title || 'Duration'}
                      color="#ec4899"
                      fullWidth
                    >
                      <DurationSelectMenu
                        allowEnteringValue={hasDrug}
                        selectedDurationTime={drugAdviceRow?.duration}
                        prescriptionViewSettings={prescriptionViewSettings}
                        menuPortalTarget={document.body}
                        setSelectedDurationTime={(newValue) =>
                          handleDrugAdviceChange('duration', drugAdviceRowIndex, newValue, setDrugsAdvice)
                        }
                        options={drugAdviceDurations}
                      />
                    </MobileFieldWrapper>
                  )}

                  {/* Row 4: Notes */}
                  {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
                    <MobileFieldWrapper
                      label={drugAdviceSubSections?.find((item) => item?.uid === 'notes')?.title || 'Notes'}
                      color="#64748b"
                      fullWidth
                    >
                      <NotesSelectMenu
                        idx={drugAdviceRowIndex}
                        allowEnteringValue={hasDrug}
                        selectedNote={drugAdviceRow?.notes}
                        prescriptionViewSettings={prescriptionViewSettings}
                        isDrugNew={drugAdviceRow?.drug?.__isNew__}
                        menuPortalTarget={document.body}
                        setSelectedNote={(newValue) =>
                          handleDrugAdviceChange('notes', drugAdviceRowIndex, newValue, setDrugsAdvice)
                        }
                        options={drugAdviceNotes}
                      />
                    </MobileFieldWrapper>
                  )}
                </Box>
              </Collapse>
            </Box>
          )
        })}

        {/* Add New Prescription Button */}
        <Box
          onClick={() => handleAddDrugAdviceItem(setDrugsAdvice)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 2.5,
            borderRadius: '16px',
            border: '2px dashed rgba(67, 56, 202, 0.3)',
            background: 'linear-gradient(145deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.04) 100%)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'rgba(67, 56, 202, 0.5)',
              background: 'linear-gradient(145deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.08) 100%)',
              transform: 'scale(1.01)',
            },
            '&:active': {
              transform: 'scale(0.99)',
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(67, 56, 202, 0.25)',
            }}
          >
            <AddCircleOutline sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography
            sx={{
              color: '#4338ca',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Add Medication
          </Typography>
        </Box>
      </Box>
    )
  }

  // Desktop Table View (Original)
  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(67, 56, 202, 0.12)',
        background: 'white',
        boxShadow: '0 2px 8px rgba(67, 56, 202, 0.06)',
        overflowX: 'auto',
        colorScheme: 'light', // Force light mode to prevent dark mode inheritance
        color: '#1f2937', // Explicit dark text color to ensure visibility
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(67, 56, 202, 0.05)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(67, 56, 202, 0.2)',
          borderRadius: '10px',
          '&:hover': {
            background: 'rgba(67, 56, 202, 0.3)',
          },
        },
      }}
    >
      {/* default row */}
      <PMPSingleRowForDrugs isHeaderRow useGreyBorders>
        <PMPSingleRowBox style={{ width: '3%' }} centered borderRight isHeaderRow>
          #
        </PMPSingleRowBox>
        {/* do not change the order. or improve so that order does not matter */}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'type',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'type')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'drug_name',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'drug_name')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'dosage',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'dosage')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'when',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'when')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'frequency',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'frequency')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'duration',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'duration')?.title}
          </PMPSingleRowBox>
        )}
        {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDrugTableCellWidth(
                'notes',
                94,
                getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {drugAdviceSubSections?.find((item) => item?.uid === 'notes')?.title}
          </PMPSingleRowBox>
        )}
        <div style={{ width: '3%' }}></div>
      </PMPSingleRowForDrugs>

      {/* to be rendered a number of times */}
      {drugsAdvice?.map((drugAdviceRow, drugAdviceRowIndex) => (
        <PMPSingleRowForDrugs
          useGreyBorders
          isLastRow={drugAdviceRowIndex + 1 === drugsAdvice?.length}
          key={drugAdviceRowIndex}
        >
          {/* do not change the order. or improve so that order does not matter */}
          <PMPSingleRowBox style={{ width: '3%' }} centered>
            {drugAdviceRowIndex + 1}
          </PMPSingleRowBox>

          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('type') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'type',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
              }}
            >
              <DrugTypeSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedType={drugAdviceRow?.drugType}
                options={drugAdviceTypes}
                prescriptionViewSettings={prescriptionViewSettings}
                setSelectedType={(newValue) =>
                  handleDrugAdviceChange(
                    'drugType',
                    drugAdviceRowIndex,
                    newValue,
                    setDrugsAdvice,
                    drugAdviceTypes,
                  )
                }
              />
              {/* // this is expanding full width, and making the flexgrow container expand too, i need flexGrow to be respoected and it should impose a max width on children like bbelow */}
            </div>
          )}

          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('drug_name') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'drug_name',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                position: 'relative',
                zIndex: 20, // For dropdown, but GenericDrugComponent inside has lower z-index
              }}
            >
              <DrugSelectMenu
                clinicId={clinicId}
                clinicBrandId={clinicBrandId}
                selectedDrug={drugAdviceRow?.drug}
                recommendedDrugs={recommendedDrugIds}
                doctorSpecializations={mentorProfileData?.specializedCategories}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedDrug={(newValue, updateSelectedDrugStateOnly) => {
                  handleSelectDrugAdviceItem(
                    newValue,
                    drugAdviceRowIndex,
                    updateSelectedDrugStateOnly,
                  )
                }}
              />
              {/* // this is expanding full width, and making the flexgrow container expand too, i need flexGrow to be respoected and it should impose a max width on children like bbelow */}
            </div>
          )}

          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('dosage') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'dosage',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
              }}
            >
              <DosageSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedDosage={drugAdviceRow?.dosage}
                options={drugAdviceDosages}
                prescriptionViewSettings={prescriptionViewSettings}
                setSelectedDosage={(newValue) =>
                  handleDrugAdviceChange('dosage', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
              />
            </div>
          )}

          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('when') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'when',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
              }}
            >
              <WhatTimeSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedWhatTime={drugAdviceRow?.whatTime}
                prescriptionViewSettings={prescriptionViewSettings}
                setSelectedWhatTime={(newValue) =>
                  handleDrugAdviceChange('whatTime', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceWhatTimeOptions}
              />
            </div>
          )}
          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('frequency') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'frequency',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
                position: 'relative',
                zIndex: 25, // Higher than Generic Name component (zIndex: 1) to appear in front
              }}
            >
              <FrequencySelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedFrequency={drugAdviceRow?.frequency}
                prescriptionViewSettings={prescriptionViewSettings}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                setSelectedFrequency={(newValue) =>
                  handleDrugAdviceChange('frequency', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceFrequencies}
              />
            </div>
          )}
          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('duration') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'duration',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
              }}
            >
              <DurationSelectMenu
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedDurationTime={drugAdviceRow?.duration}
                prescriptionViewSettings={prescriptionViewSettings}
                setSelectedDurationTime={(newValue) =>
                  handleDrugAdviceChange('duration', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceDurations}
              />
            </div>
          )}
          {getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections).has('notes') && (
            <div
              style={{
                width: getPercentageOfItemForDrugTableCellWidth(
                  'notes',
                  94,
                  getRenderDrugAdviceSubSectionsSet(drugAdviceSubSections),
                ),
              }}
            >
              <NotesSelectMenu
                idx={drugAdviceRowIndex}
                allowEnteringValue={Boolean(drugAdviceRow?.drug)}
                selectedNote={drugAdviceRow?.notes}
                prescriptionViewSettings={prescriptionViewSettings}
                isDrugNew={drugAdviceRow?.drug?.__isNew__}
                setSelectedNote={(newValue) =>
                  handleDrugAdviceChange('notes', drugAdviceRowIndex, newValue, setDrugsAdvice)
                }
                options={drugAdviceNotes}
              />
            </div>
          )}
          <PMPSingleRowBox style={{ width: '3%' }} whiteBG centered>
            <Tooltip title="Delete prescription" arrow>
              <IconButton
                onClick={() => handleDeleteDrugAdviceItem(drugAdviceRowIndex, setDrugsAdvice)}
                size="small"
                sx={{
                  color: '#ef4444',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DeleteOutline sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </PMPSingleRowBox>
        </PMPSingleRowForDrugs>
      ))}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          borderTop: '1px solid rgba(67, 56, 202, 0.1)',
          background: 'rgba(67, 56, 202, 0.02)',
        }}
      >
        <Tooltip title="Add new prescription" arrow>
          <IconButton
            onClick={() => handleAddDrugAdviceItem(setDrugsAdvice)}
            sx={{
              color: '#4338ca',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <AddCircleOutline sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// Mobile Field Wrapper Component
const MobileFieldWrapper = ({ label, color, fullWidth, children }) => (
  <Box
    sx={{
      flex: fullWidth ? '1 1 100%' : '1 1 calc(50% - 6px)',
      minWidth: fullWidth ? '100%' : 0,
      position: 'relative',
    }}
  >
    <Box
      component="div"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: color,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      {label}
    </Box>
    <Box
      sx={{
        borderRadius: '10px',
        overflow: 'visible',
        border: `1px solid ${color}20`,
        background: '#fff',
        transition: 'all 0.2s ease',
        '&:focus-within': {
          borderColor: color,
          boxShadow: `0 0 0 2px ${color}15`,
        },
        // Ensure dropdowns appear above other elements
        '& .css-1nmdiq5-menu, & [class*="-menu"]': {
          zIndex: 9999,
        },
      }}
    >
      {children}
    </Box>
  </Box>
)

export default memo(DrugAdviceInput)

export function getPercentageOfItemForDrugTableCellWidth(
  sectionId,
  availableSpaceInPercentage,
  setOfVisibleItems,
) {
  const weightForThisSection = DRUG_TABLE_COLUMN_WEIGHTS?.[sectionId]

  const weightSum = Object.entries(DRUG_TABLE_COLUMN_WEIGHTS)
    .filter(([key, value]) => setOfVisibleItems.has(key))
    .reduce((acc, [key, value]) => acc + value, 0)
  return `${(weightForThisSection / weightSum) * availableSpaceInPercentage}%`
}
